import { Category } from '@/apis/providers/types/provider.types'
import { PayProviderLayout } from '@/layouts/providers/PayProviderLayout'
import { Provider } from '@/schemas/provider.schema'
import { SelectedPurse } from '@/storage/applications/providerPaySlice'
import { selectProgramSettings } from '@/storage/program_settings/programSettingsSlice'
import { MutationStatus } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import ReimbursementsAllocateCategoriesPage from '../reimbursements/new/pages/allocate_categories.page'
import { NewReimubrusementCaptureImages } from '../reimbursements/new/pages/upload_images.page'
import { AddDetails } from './pages/add_payment_details.page'
import { ChoosePurses } from './pages/choose_purses/choose_purses.page'
import { ConfirmProvider } from './pages/confirm_pay_provider.page'
import ReviewProviderPage from './pages/review/review_pay_provider.page'
import { SelectExpenseTypePage } from './pages/select_expense_categories/select_expense_categories.page'
import { SelectProvider } from './pages/select_pay_provider.page'
import {
  ProviderPaymentStep,
  ProviderPaymentStepType,
} from './provider.constants'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

export type PaymentProps<T> = {
  onNext: (data: T) => void
}

export type ProviderPaymentDetails = {
  provider: Provider
  invoiceNumber: string
  amount: number
  documents: string[]
  purses: SelectedPurse[]
  expenseCategories: Category[]
  comments: string
  baseAmount: number
}

type Props = {
  defaultStep?: ProviderPaymentStepType
  defaultValues?: ProviderPaymentDetails
  onSubmit: (paymentDetails: ProviderPaymentDetails) => void
  mutationStatus: MutationStatus
  onExitFormBackClick: () => void
}

// Component to handle the provider payment flow
const CreateProviderPaymentForm = (props: Props) => {
  const {
    defaultValues,
    mutationStatus,
    onSubmit,
    defaultStep,
    onExitFormBackClick,
  } = props
  const [currentStep, setCurrentStep] = useState<ProviderPaymentStepType>(
    defaultStep ?? ProviderPaymentStep.SELECT_PROVIDER
  )
  const [paymentData, setPaymentData] = useState<ProviderPaymentDetails>(() => {
    if (defaultValues) {
      return {
        provider: {
          email: defaultValues.provider.email,
          id: defaultValues.provider.id,
          location: defaultValues.provider.location,
          name: defaultValues.provider.name,
        },
        invoiceNumber: defaultValues.invoiceNumber,
        amount: defaultValues.amount,
        baseAmount: defaultValues.baseAmount,
        documents: defaultValues.documents,
        purses: defaultValues.purses,
        expenseCategories: defaultValues.expenseCategories,
        comments: defaultValues.comments,
      }
    }

    return {
      provider: {
        id: 0,
        name: '',
        email: '',
        location: '',
      },
      invoiceNumber: '',
      amount: 0,
      baseAmount: 0,
      documents: [],
      purses: [],
      expenseCategories: [],
      comments: '',
    }
  })
  const navigate = useNavigate()
  const programSettings = useSelector(selectProgramSettings)

  useEffect(() => {
    if (programSettings.pay_provider_module == 0) navigate(-1)
  }, [])

  // Function to move to the next step
  const goToNextStep = (nextStep: ProviderPaymentStepType) => {
    setCurrentStep(nextStep)
  }

  // Function to update payment data
  function updatePaymentData(data: Partial<ProviderPaymentDetails>) {
    setPaymentData((prevData) => ({ ...prevData, ...data }))
  }

  // Function to go back to the previous step
  const goToPreviousStep = () => {
    switch (currentStep) {
      case ProviderPaymentStep.SELECT_PROVIDER:
        onExitFormBackClick()
        break

      case ProviderPaymentStep.CONFIRM_PROVIDER:
        setCurrentStep(ProviderPaymentStep.SELECT_PROVIDER)
        break
      case ProviderPaymentStep.ADD_DETAILS:
        setCurrentStep(ProviderPaymentStep.CONFIRM_PROVIDER)
        break
      case ProviderPaymentStep.UPLOAD_DOCUMENTS:
        setCurrentStep(ProviderPaymentStep.ADD_DETAILS)
        break
      case ProviderPaymentStep.CHOOSE_PURSES:
        setCurrentStep(ProviderPaymentStep.UPLOAD_DOCUMENTS)
        break
      case ProviderPaymentStep.SELECT_EXPENSE_TYPE:
        setCurrentStep(ProviderPaymentStep.CHOOSE_PURSES)
        break
      case ProviderPaymentStep.EXPENSE_ALLOCATION:
        setCurrentStep(ProviderPaymentStep.SELECT_EXPENSE_TYPE)
        break
      case ProviderPaymentStep.REVIEW:
        setCurrentStep(ProviderPaymentStep.EXPENSE_ALLOCATION)
        break
    }
  }

  // Render the appropriate component based on the current step
  const renderCurrentStep = () => {
    console.log(paymentData.amount)

    switch (currentStep) {
      case ProviderPaymentStep.SELECT_PROVIDER:
        return (
          <SelectProvider
            onNext={(provider) => {
              updatePaymentData({
                provider: {
                  name: provider.name,
                  email: provider.email,
                  id: provider.id,
                  location: provider.location,
                },
              })
              goToNextStep(ProviderPaymentStep.CONFIRM_PROVIDER)
            }}
          />
        )
      case ProviderPaymentStep.CONFIRM_PROVIDER:
        return (
          <ConfirmProvider
            onBack={goToPreviousStep}
            provider={paymentData.provider}
            onNext={() => {
              goToNextStep(ProviderPaymentStep.ADD_DETAILS)
            }}
          />
        )
      case ProviderPaymentStep.ADD_DETAILS:
        return (
          <AddDetails
            onBack={goToPreviousStep}
            provider={paymentData.provider}
            defaultTotal={paymentData.amount}
            onNext={(data) => {
              updatePaymentData({
                amount: data.payment_amount,
              })
              goToNextStep(ProviderPaymentStep.UPLOAD_DOCUMENTS)
            }}
          />
        )
      case ProviderPaymentStep.UPLOAD_DOCUMENTS:
        return (
          <NewReimubrusementCaptureImages
            onUploadedDocumentRemove={() => {}}
            defaultValues={paymentData.documents}
            onNext={(documents) => {
              updatePaymentData({
                documents: [
                  ...paymentData.documents,
                  ...documents.map((file) => file),
                ],
              })
              goToNextStep(ProviderPaymentStep.CHOOSE_PURSES)
            }}
          />
        )
      case ProviderPaymentStep.CHOOSE_PURSES:
        return (
          <ChoosePurses
            amount={paymentData.amount}
            onBack={goToPreviousStep}
            purses={paymentData.purses}
            onNext={(purses) => {
              updatePaymentData({
                purses,
              })
              goToNextStep(ProviderPaymentStep.SELECT_EXPENSE_TYPE)
            }}
          />
        )

      case ProviderPaymentStep.SELECT_EXPENSE_TYPE:
        return (
          <SelectExpenseTypePage
            defaultValues={{
              comments: paymentData.comments,
              expenseCategories: paymentData.expenseCategories,
              invoiceNumber: paymentData.invoiceNumber,
            }}
            onBack={goToPreviousStep}
            onNext={(data) => {
              updatePaymentData({
                expenseCategories: data.expenseCategories,
                comments: data.comments,
                invoiceNumber: data.invoiceNumber,
              })
              goToNextStep(ProviderPaymentStep.EXPENSE_ALLOCATION)
            }}
          />
        )
      case ProviderPaymentStep.EXPENSE_ALLOCATION:
        return (
          <ReimbursementsAllocateCategoriesPage
            amount={paymentData.amount}
            onNext={(categories) => {
              updatePaymentData({
                expenseCategories: categories,
              })
              goToNextStep(ProviderPaymentStep.REVIEW)
            }}
            defaultValues={paymentData.expenseCategories}
          />
        )
      case ProviderPaymentStep.REVIEW:
        return (
          <ReviewProviderPage
            mutationStatus={mutationStatus}
            onSubmit={() => {
              onSubmit(paymentData)
            }}
            paymentDetails={paymentData}
          />
        )
      default:
        return (
          <SelectProvider
            onNext={(provider) => {
              updatePaymentData({
                provider,
              })
              goToNextStep(ProviderPaymentStep.CONFIRM_PROVIDER)
            }}
          />
        )
    }
  }

  return (
    <PayProviderLayout currentStep={currentStep} onBack={goToPreviousStep}>
      <div className="provider-payment-stepper">{renderCurrentStep()}</div>
    </PayProviderLayout>
  )
}

export default CreateProviderPaymentForm
